/*
 * Product card — transition + variant helpers. Extracted from
 * product-card.js to keep each file under the 150-line JS cap.
 * Exposed on window.BroraProductCardVariant so product-card.js can call
 * into it without relying on Shopify's asset-URL rewriting for ES imports.
 *
 * showSlide / showFade manage both transforms AND opacity on each slide:
 * each mode resets the OTHER axis to its resting state so a hover crossfade
 * that follows an arrow click doesn't leave a slide stranded at
 * translate-x-full (orch_PRODUCT_CARD.md Risk #1).
 */
(function () {
  'use strict';

  var LG = window.matchMedia('(min-width: 1024px)');

  function setSwatchActive(active, swatches) {
    swatches.forEach(function (s) {
      s.setAttribute('aria-current', s === active ? 'true' : 'false');
    });
  }

  function applyVariantMap(card, swatch) {
    var raw = swatch.dataset.swatchVariants;
    if (!raw) return;
    var map;
    try { map = JSON.parse(raw); } catch (_) { return; }
    if (!map || typeof map !== 'object') return;
    // Parallel pre-order map ({ size_label: selling plan id | false }) keeps each size button's
    // data-preorder in sync with the selected swatch, so quick-add routing (product-card.js) stays
    // correct after a colour change. The plan id is written through as the attribute value because the
    // market gate (preorder-market-scope.js) reads it to decide whether the offer applies here.
    var preMap = null;
    var rawPre = swatch.dataset.swatchPreorder;
    if (rawPre) { try { preMap = JSON.parse(rawPre); } catch (_) { preMap = null; } }
    card.querySelectorAll('[data-quick-add-size]').forEach(function (btn) {
      var key = btn.textContent.trim();
      if (Object.prototype.hasOwnProperty.call(map, key)) {
        btn.dataset.variantId = String(map[key]);
      }
      if (preMap && Object.prototype.hasOwnProperty.call(preMap, key)) {
        if (preMap[key]) btn.setAttribute('data-preorder', String(preMap[key]));
        else btn.removeAttribute('data-preorder');
      }
    });
    // The remap re-arms every pill from the swatch map, which is market-blind, so the market gate must
    // run again over this card.
    if (window.BroraPreorderMarket) window.BroraPreorderMarket.gateCards(card);
  }

  function showFade(outgoing, incoming) {
    // S17 — reset the slide axis silently so the first hover doesn't animate
    // a stale translate. Add transition-none, snap to translate-x-0, force a
    // reflow, then re-enable transitions before the actual fade.
    [outgoing, incoming].forEach(function (el) {
      el.classList.add('transition-none');
      el.classList.remove('translate-x-full', '-translate-x-full');
      el.classList.add('translate-x-0');
    });
    void incoming.offsetWidth;
    [outgoing, incoming].forEach(function (el) { el.classList.remove('transition-none'); });
    outgoing.classList.replace('opacity-100', 'opacity-0');
    incoming.classList.replace('opacity-0', 'opacity-100');
  }

  function showSlide(outgoing, incoming, forward) {
    // S9 — snap opacity to resting state without animating it.
    // Critically, also snap incoming to its off-screen start position here,
    // BEFORE re-enabling transitions. showFade resets all slides to
    // translate-x-0, so if we position incoming after transitions are live
    // the browser animates it off-screen before sliding it back in.
    [outgoing, incoming].forEach(function (el) {
      el.classList.add('transition-none');
      el.classList.replace('opacity-0', 'opacity-100');
    });
    incoming.classList.remove('translate-x-0', 'translate-x-full', '-translate-x-full');
    incoming.classList.add(forward ? 'translate-x-full' : '-translate-x-full');
    void incoming.offsetWidth;
    [outgoing, incoming].forEach(function (el) { el.classList.remove('transition-none'); });
    outgoing.classList.remove('translate-x-0', 'translate-x-full', '-translate-x-full');
    outgoing.classList.add(forward ? '-translate-x-full' : 'translate-x-full');
    incoming.classList.remove('translate-x-full', '-translate-x-full');
    incoming.classList.add('translate-x-0');
  }

  // S6 — drawer factory. Lives here (not product-card.js) to keep the latter
  // under the 150-line JS cap. tapOutside is the only document.* listener in
  // the orch; lifecycle: bound in open(), unbound in close() and on
  // _tapOutside delete.
  function makeDrawer(card, drawer, trigger, sizeBtns) {
    function open(opts) {
      if (!drawer) return;
      card.setAttribute('data-quick-add-open', '');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      // Move focus into the drawer only on a KEYBOARD open. On a mouse open,
      // programmatic focus makes Safari show the size's :focus-visible purple
      // ring — WebKit doesn't focus the trigger <button> on click, so the
      // focus-visible suppression chain breaks and script focus defaults to
      // visible. Focus the radio (not the <label>) so keyboard focus is
      // reliable cross-browser — label.focus() is a no-op in Chrome/Firefox.
      if (opts && opts.viaKeyboard) {
        var first = sizeBtns.find(function (b) { return !b.hasAttribute('aria-disabled'); });
        if (first) {
          var firstInput = first.querySelector('input[type="radio"]') || first;
          firstInput.focus({ preventScroll: true });
        }
      }
      var tapOutside = function (e) { if (!card.contains(e.target)) close({ returnFocus: false }); };
      document.addEventListener('pointerdown', tapOutside);
      card._tapOutside = tapOutside;
    }
    function close(opts) {
      if (!drawer) return;
      var returnFocus = !opts || opts.returnFocus !== false;
      card.removeAttribute('data-quick-add-open');
      if (card._tapOutside) {
        document.removeEventListener('pointerdown', card._tapOutside);
        delete card._tapOutside;
      }
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
        if (returnFocus) trigger.focus({ preventScroll: true });
      }
    }
    return { open: open, close: close };
  }

  // On bfcache restore the DOM keeps the clicked swatch/image — reset every live
  // card to its default via its stored reset (set in bindSwatches; bound once).
  var _bfcacheBound = false;
  function bindBfcacheReset() {
    if (_bfcacheBound) return;
    _bfcacheBound = true;
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      document.querySelectorAll('[data-product-card], [data-voyado-card]').forEach(function (c) {
        if (typeof c._bfcacheReset === 'function') c._bfcacheReset();
      });
    });
  }

  function bindSwatches(card, swatches, counterWrap) {
    // Sibling colours are previewed by fading a single overlay image over the
    // gallery ([data-card-preview]) — they no longer live in the gallery as slides.
    // Each swatch carries its preview URL; the current colour's is blank (hovering
    // it just reveals the product's own gallery).
    var previewEl = card.querySelector('[data-card-preview]');
    function showPreview(url) {
      if (previewEl) {
        if (url) {
          if (previewEl.getAttribute('src') !== url) previewEl.src = url;
          previewEl.style.opacity = '1';
        } else {
          previewEl.style.opacity = '0';
        }
      }
      // Hide the gallery counter while previewing a sibling — its "n/N" is the own
      // gallery position, which doesn't apply to a single preview image.
      if (counterWrap) counterWrap.style.opacity = url ? '0' : '';
    }
    // Hover intent. The chips measure 20px with a 2px gap, so a cursor moving from one swatch to
    // another crosses every chip in between and each crossing used to commit a full, instantly-painted
    // preview (this overlay carries no transition, by design) — the image appeared to blink through
    // colours the shopper never aimed at. One timer per card gates BOTH the show and the hide:
    // mouseleave/mouseenter across adjacent chips fire in the same tick (sub-millisecond, measured), so
    // cancelling the queued hide lets a transit replace the outgoing preview directly instead of
    // flashing the gallery underneath it. 100ms is the largest delay still inside the ~100ms
    // perceived-immediate threshold; against a 20px chip it suppresses every transit above 200px/s
    // while a genuinely slow scan along the row still previews each colour.
    // Keyboard focus is deliberately NOT debounced — Tab lands only where the shopper aimed, so there
    // is no transit to suppress, and delaying it would blank the preview while tabbing along the row.
    var INTENT_MS = 100;
    var intentTimer = null;
    function cancelIntent() {
      if (intentTimer) { clearTimeout(intentTimer); intentTimer = null; }
    }
    function queuePreview(url) {
      cancelIntent();
      intentTimer = setTimeout(function () { intentTimer = null; showPreview(url); }, INTENT_MS);
    }
    var warmed = false;
    card.addEventListener('mouseenter', function () {
      if (warmed) return;
      warmed = true;
      // Warm the cache on first card hover so each colour's first preview is instant.
      swatches.forEach(function (s) { var u = s.dataset.swatchPreview; if (u) { new Image().src = u; } });
    });
    swatches.forEach(function (swatch) {
      var url = swatch.dataset.swatchPreview;
      if (LG.matches) {
        swatch.addEventListener('mouseenter', function () { queuePreview(url); });
        swatch.addEventListener('mouseleave', function () { queuePreview(''); });
        swatch.addEventListener('focus', function () { cancelIntent(); showPreview(url); });
        swatch.addEventListener('blur', function () { cancelIntent(); showPreview(''); });
      }
      swatch.addEventListener('click', function (e) {
        setSwatchActive(swatch, swatches);
        applyVariantMap(card, swatch);
        var u = swatch.dataset.swatchUrl;
        if (u) { window.location.href = u; return; }
        e.preventDefault();
      });
    });

    // Reset to the default (first) swatch + own gallery on a bfcache restore.
    card._bfcacheReset = function () {
      if (!swatches.length) return;
      setSwatchActive(swatches[0], swatches);
      applyVariantMap(card, swatches[0]);
      cancelIntent();
      showPreview('');
    };
    bindBfcacheReset();
  }

  function observeScroll(gallery, slides, onChange) {
    var io = new IntersectionObserver(function (entries) {
      // Desktop stacks the slides absolutely (all intersect the root at once); this observer only tracks
      // the mobile snap-scroll position.
      if (LG.matches) return;
      for (var j = 0; j < entries.length; j++) {
        if (!entries[j].isIntersecting) {
          entries[j].target.setAttribute('aria-hidden', 'true');
          continue;
        }
        entries[j].target.removeAttribute('aria-hidden');
        var idx = slides.indexOf(entries[j].target);
        if (idx !== -1) onChange(idx);
      }
    }, { root: gallery, threshold: 0.5 });
    slides.forEach(function (s) { io.observe(s); });
  }

  // Slides past the first ship their URLs parked in data attributes and carry no source attribute at
  // all. Native lazy loading cannot defer them: at lg every slide is positioned over the active
  // slide's box (the same stacking observeScroll notes above), so all of them qualify as
  // near-viewport together and the whole gallery downloads before the shopper touches anything.
  // Promote the candidate list before the fallback so the browser never issues a throwaway request
  // for the single-URL source. Idempotent: a slide with nothing parked is a no-op.
  function hydrateSlide(slide) {
    if (!slide) return;
    var img = slide.querySelector('img[data-src]');
    if (!img) return;
    var parkedSet = img.getAttribute('data-srcset');
    if (parkedSet) {
      img.setAttribute('srcset', parkedSet);
      img.removeAttribute('data-srcset');
    }
    img.setAttribute('src', img.getAttribute('data-src'));
    img.removeAttribute('data-src');
  }

  // Mobile only — the caller gates on LG. Below lg the slides sit side by side inside the horizontal
  // snap-scroller, so they DO have distinct geometry and an observer scoped to that scroller works.
  // The inline margin gives one container-width of look-ahead; the block margin MUST stay 0. On a card
  // rendered with its gallery disabled the scroller is a plain block and the slides stack vertically
  // past its bottom edge — a block margin would pull every one of them in and undo the deferral, while
  // 0 correctly leaves them alone (those cards never navigate below lg).
  function observeHydration(gallery, slides) {
    if (!gallery || !slides || !slides.length || typeof IntersectionObserver !== 'function') return null;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        hydrateSlide(entry.target);
        io.unobserve(entry.target);
      });
    }, { root: gallery, rootMargin: '0px 100%' });
    slides.forEach(function (s) {
      if (s.querySelector('img[data-src]')) io.observe(s);
    });
    return io;
  }

  window.BroraProductCardVariant = {
    setSwatchActive: setSwatchActive,
    applyVariantMap: applyVariantMap,
    showSlide: showSlide,
    showFade: showFade,
    makeDrawer: makeDrawer,
    bindSwatches: bindSwatches,
    observeScroll: observeScroll,
    hydrateSlide: hydrateSlide,
    observeHydration: observeHydration,
  };
})();
